/**
 * `artworks` — les œuvres (base galerie, 4.1 et 4.2).
 *
 * Le statut est un champ explicite, mais il est posé par les événements
 * (D13, voir `status.ts`). Les prix sont des entiers de centimes, hors taxes.
 */
import { z } from 'zod';
import { regimeTvaSchema } from '../tax/regimes.js';
import { bilingueSchema, CHAMPS_DOCUMENT, centimesPositifsSchema, deviseSchema, dimensionsSchema, idSchema, slugSchema, withAudit, } from './commun.js';
/** Statuts d'une œuvre (4.2, D13). */
export const STATUTS_OEUVRE = [
    'disponible',
    'reservee',
    'vendue',
    'en_depot',
    'pretee',
    'louee',
    'retiree',
    'archivee',
];
export const statutOeuvreSchema = z.enum(STATUTS_OEUVRE);
/** Une œuvre est unique, ou tirée en édition. */
export const TYPES_OEUVRE = ['unique', 'edition'];
export const typeOeuvreSchema = z.enum(TYPES_OEUVRE);
/**
 * Le tirage d'une œuvre en édition. Les exemplaires ne sont créés dans
 * `editions` que si la galerie veut les suivre un par un (4.2) ; sinon, un
 * compteur suffit.
 */
export const tirageSchema = z.object({
    taille: z.int().min(1),
    epreuvesArtiste: z.int().min(0).default(0),
    epreuvesEssai: z.int().min(0).default(0),
    /** Les exemplaires sont-ils suivis un par un dans `editions` ? */
    exemplairesSuivis: z.boolean().default(false),
    vendus: z.int().min(0).default(0),
});
export const artworkSchema = withAudit(z.object({
    _id: idSchema,
    /** Le titre d'une œuvre ne se traduit pas (4.2). */
    titre: z.string().min(1),
    slug: slugSchema,
    artisteId: idSchema,
    annee: z.int().min(0).max(2999).optional(),
    technique: bilingueSchema.optional(),
    dimensions: dimensionsSchema.optional(),
    type: typeOeuvreSchema.default('unique'),
    tirage: tirageSchema.optional(),
    numeroInventaire: z.string().min(1),
    prixPublicCentimes: centimesPositifsSchema.optional(),
    prixArtisteCentimes: centimesPositifsSchema.optional(),
    devise: deviseSchema.default('EUR'),
    regimeTva: regimeTvaSchema.default('normal'),
    /** Prix d'acquisition, nécessaire à la TVA sur la marge. */
    prixAchatCentimes: centimesPositifsSchema.optional(),
    droitDeSuite: z.boolean().default(false),
    statut: statutOeuvreSchema.default('disponible'),
    localisationId: idSchema.optional(),
    mediaPrincipalId: idSchema.optional(),
    visibleSurLeSite: z.boolean().default(false),
    prixVisible: z.boolean().default(false),
    description: bilingueSchema.optional(),
    notesInternes: z.string().optional(),
    etiquettes: z.array(z.string()).default([]),
}));
export const artworkCreationSchema = artworkSchema.omit({ ...CHAMPS_DOCUMENT, statut: true });
/** Le statut ne se modifie pas par la mise à jour : il vient des événements. */
export const artworkMiseAJourSchema = artworkCreationSchema.partial();
/** La marge est dérivée, jamais stockée (4.2). */
export function margeCentimes(oeuvre) {
    if (oeuvre.prixPublicCentimes === undefined || oeuvre.prixArtisteCentimes === undefined) {
        return undefined;
    }
    return oeuvre.prixPublicCentimes - oeuvre.prixArtisteCentimes;
}
//# sourceMappingURL=artworks.js.map